import { useEffect, useState } from "react";
import { saveFile } from "./documentDownload";
import type { SaveOutcome } from "./documentDownload";

export const QR_CODE_FILE_NAME = "payment-qr-code.png";
export const QR_CODE_MIME_TYPE = "image/png";

// SVG sources frequently report no intrinsic size; fall back to a fixed square.
const QR_FALLBACK_SIZE = 512;

// Vector sources render at their default size (often 150px), which is small for
// a scannable QR once flattened to PNG.
const QR_MIN_SIZE = 512;

const SVG_DATA_URL_PREFIX = "data:image/svg+xml";
const BASE64_MARKER = ";base64,";

// atob only accepts whole 4-character groups, so decode a prefix that is long
// enough for every signature below (12 bytes) and still group-aligned.
function decodeBase64Prefix(base64: string): string {
  const head = base64.slice(0, 24);
  const aligned = head.slice(0, head.length - (head.length % 4));
  if (!aligned) return "";

  try {
    return atob(aligned);
  } catch {
    return "";
  }
}

// The QR payload arrives labelled image/png even when it is SVG, and a browser
// that trusts the label refuses to decode it and draws a broken image instead.
// Sniff the actual bytes rather than believing the declared type.
function sniffImageMimeType(bytes: string): string {
  if (bytes.startsWith("\x89PNG")) return "image/png";
  if (bytes.startsWith("\xFF\xD8\xFF")) return "image/jpeg";
  if (bytes.startsWith("GIF8")) return "image/gif";
  if (bytes.startsWith("RIFF") && bytes.slice(8, 12) === "WEBP") return "image/webp";

  const markup = bytes.replace(/^﻿/, "").trimStart();
  if (markup.startsWith("<?xml") || markup.startsWith("<svg") || markup.startsWith("<!DOCTYPE svg")) {
    return "image/svg+xml";
  }

  return "";
}

function toImageDataUrl(rawBase64: string): string {
  // Some backends wrap base64 payloads at 76 characters (PHP chunk_split style).
  const base64 = rawBase64.replace(/\s/g, "");
  if (!base64) return "";

  const mimeType = sniffImageMimeType(decodeBase64Prefix(base64));

  // Not an image at all — most often a raw PromptPay payload instead of a
  // rendered QR. Report it rather than handing the browser undrawable bytes.
  if (!mimeType) {
    console.error("Unrecognised QR code payload:", base64.slice(0, 32));
    return "";
  }

  return `data:${mimeType};base64,${base64}`;
}

export function normalizeQrCode(raw: string): string {
  if (!raw) return "";

  const value = String(raw).trim();
  if (!value) return "";

  if (/^(https?:|blob:)/i.test(value)) {
    return value;
  }

  // Raw (non-base64) SVG markup
  if (value.startsWith("<?xml") || value.startsWith("<svg") || value.startsWith("<!DOCTYPE svg")) {
    return `${SVG_DATA_URL_PREFIX};charset=utf-8,${encodeURIComponent(value)}`;
  }

  if (value.startsWith("data:")) {
    const markerIndex = value.indexOf(BASE64_MARKER);
    // A non-base64 data URL (percent-encoded SVG) already carries a usable payload.
    if (markerIndex === -1) return value;

    return toImageDataUrl(value.slice(markerIndex + BASE64_MARKER.length));
  }

  return toImageDataUrl(value);
}

export function qrCodeToPngBlob(qrCodeUrl: string) {
  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      let width = img.naturalWidth || img.width || QR_FALLBACK_SIZE;
      let height = img.naturalHeight || img.height || QR_FALLBACK_SIZE;

      if (qrCodeUrl.startsWith(SVG_DATA_URL_PREFIX) && Math.max(width, height) < QR_MIN_SIZE) {
        const scale = QR_MIN_SIZE / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas is not supported"));
        return;
      }

      // QR sources may be transparent, so paint a white ground first —
      // otherwise the code can end up unreadable once flattened.
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("Failed to encode QR code image"));
      }, "image/png");
    };

    img.onerror = () => reject(new Error("Failed to load QR code image"));

    // A remote source taints the canvas unless it opts into CORS.
    if (/^https?:/i.test(qrCodeUrl)) {
      img.crossOrigin = "anonymous";
    }

    img.src = qrCodeUrl;
  });
}

export async function qrCodeToPngFile(qrCodeUrl: string) {
  const blob = await qrCodeToPngBlob(qrCodeUrl);
  return new File([blob], QR_CODE_FILE_NAME, { type: QR_CODE_MIME_TYPE });
}

function readAsDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export interface PreparedQrCode {
  sourceUrl: string;
  file: File;
  // PNG data URL. The API often sends SVG, which a long-press cannot save to
  // Photos and banking apps cannot read, so show this instead.
  previewUrl: string;
}

// Render the PNG as soon as the QR code arrives rather than on tap: the share
// sheet only opens while the tap is fresh, and decoding can outlast that.
export function usePreparedQrCode(qrCodeUrl: string) {
  const [prepared, setPrepared] = useState<PreparedQrCode | null>(null);

  useEffect(() => {
    if (!qrCodeUrl) return;

    let cancelled = false;
    qrCodeToPngFile(qrCodeUrl)
      .then(async file => {
        const previewUrl = await readAsDataUrl(file);
        if (!cancelled) {
          setPrepared({ sourceUrl: qrCodeUrl, file, previewUrl });
        }
      })
      .catch(error => console.error("Error preparing QR code image:", error));

    return () => {
      cancelled = true;
    };
  }, [qrCodeUrl]);

  return prepared?.sourceUrl === qrCodeUrl ? prepared : null;
}

export function saveQrCode(qrCodeUrl: string, prepared: PreparedQrCode | null, openExternal?: () => void) {
  return saveFile({
    fileName: QR_CODE_FILE_NAME,
    mimeType: QR_CODE_MIME_TYPE,
    getFile: () => prepared?.file ?? qrCodeToPngFile(qrCodeUrl),
    openExternal,
  });
}

export const QR_CODE_SAVE_MESSAGES: Record<SaveOutcome, string> = {
  saved: "บันทึก QR Code แล้ว หากไม่พบรูปในเครื่อง ให้กดค้างที่รูป QR Code แล้วเลือกบันทึกรูปภาพ",
  cancelled: "",
  retry: "QR Code พร้อมแล้ว กรุณากดปุ่มดาวน์โหลดอีกครั้ง",
  external: "กำลังเปิดในเบราว์เซอร์...",
  manual: "เบราว์เซอร์นี้บันทึกไฟล์ไม่ได้ กรุณากดค้างที่รูป QR Code แล้วเลือกบันทึกรูปภาพ",
};
